import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MapPin,
  Coins,
  Mail,
  Phone,
  Search,
  ShieldCheck,
  Lock,
  ArrowUpRight,
  Star,
} from "lucide-react";
import { PageShell } from "@/components/sections";
import { api, requireMember } from "@/lib/auth";
import { devLog } from "@/lib/log";

const SERVICE_FILTERS = [
  "All",
  "Production",
  "DJ",
  "Catering",
  "Venue",
  "Photography/Videography",
  "Hair & Make-up",
  "Event planner",
  "Deco",
  "Florist",
  "Outfits",
  "Entertainment",
];

const EXPLORE_VENDORS = [
  {
    id: "djs",
    title: "DJs",
    image: "/categories/djs.jpg",
  },
  {
    id: "production",
    title: "Production",
    image: "/categories/production.jpg",
  },
  {
    id: "content-creation",
    title: "Content Creation",
    image: "/categories/content.jpg",
  },
  {
    id: "catering",
    title: "Catering",
    image: "/categories/catering.jpg",
  },
  {
    id: "event-planner",
    title: "Event Planner",
    image: "/categories/event-planner.jpg",
  },
  {
    id: "video-photo",
    title: "Videography & Photography",
    image: "/categories/video-photo-v3.jpg",
  },
  {
    id: "decor",
    title: "Wedding Decor",
    image: "/categories/wedding-decor-v2.jpg",
  },
  {
    id: "hair-makeup",
    title: "Hair & Makeup",
    image: "/categories/hair-makeup-v2.jpg",
  },
  {
    id: "bride-101",
    title: "Bride 101",
    image: "/categories/bride-101-v2.jpg",
  },
  {
    id: "groom-101",
    title: "Groom 101",
    image: "/categories/groom-101-v2.jpg",
  },
  {
    id: "wedding-cakes",
    title: "Wedding Cakes",
    image: "/categories/wedding-cakes.jpg",
  },
  {
    id: "wedding-stationery",
    title: "Wedding Stationery",
    image: "/categories/wedding-stationery.jpg",
  },
  {
    id: "photobooth",
    title: "PhotoBooth",
    image: "/categories/photobooth.jpg",
  },
];

/* ---------- Soft access gate (non-admin) ---------- */
const AccessGate = () => (
  <section
    data-testid="vendor-listing-gate"
    className="relative px-6 md:px-12 py-24 md:py-32"
  >
    <div className="mx-auto max-w-xl text-center">
      <span className="inline-flex items-center justify-center h-12 w-12 bg-[var(--ld-ink)] text-[var(--ld-gold)]">
        <Lock size={18} strokeWidth={1.5} />
      </span>
      <h1 className="mt-6 font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--ld-ink)] font-medium leading-tight">
        Sign up to view.
      </h1>
      <p className="mt-4 text-[15px] text-[var(--ld-ink-muted)]">
        The Vendor Listing is reserved for Luxé Directory members. Sign up
        below — once you&apos;re in, we&apos;ll be in touch.
      </p>
      <div className="mt-10 flex justify-center">
        <Link
          to="/signin"
          data-testid="gate-cta-couple"
          className="inline-flex items-center justify-center gap-2 px-9 py-3.5 text-[12px] tracking-[0.2em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] hover:text-[#e9d59a] hover:bg-black transition-colors duration-300"
        >
          Sign up
        </Link>
      </div>
    </div>
  </section>
);

/* ---------- Vendor card ---------- */
const VendorCard = ({ vendor }) => {
  const tags = (vendor.service_types || []).slice(0, 4);
  const profileSrc = vendor.profile_picture_url
    ? vendor.profile_picture_url.startsWith("http")
      ? vendor.profile_picture_url
      : `${process.env.REACT_APP_BACKEND_URL}${vendor.profile_picture_url}`
    : null;
  const initial = (vendor.company_name || "?").trim().charAt(0).toUpperCase();
  return (
    <article
      data-testid={`vendor-card-${vendor.id}`}
      data-featured={vendor.is_featured ? "true" : "false"}
      className={
        "group relative flex flex-col bg-white/40 border transition-colors duration-300 p-7 " +
        (vendor.is_featured
          ? "border-[var(--ld-gold-deep)] shadow-[0_0_0_1px_var(--ld-gold-deep)] hover:border-[var(--ld-ink)]"
          : "border-[var(--ld-border)] hover:border-[var(--ld-ink)]")
      }
    >
      {vendor.is_featured && (
        <div
          data-testid={`vendor-featured-ribbon-${vendor.id}`}
          className="absolute -top-3 left-6 inline-flex items-center gap-1.5 px-3 py-1 bg-[var(--ld-gold-deep)] text-white text-[10px] tracking-[0.22em] uppercase shadow-sm"
        >
          <Star size={11} strokeWidth={2} fill="currentColor" />
          Featured Vendor of the Month
        </div>
      )}
      <div className="flex items-start gap-4">
        <div
          data-testid={`vendor-avatar-${vendor.id}`}
          className="h-14 w-14 shrink-0 overflow-hidden border border-[var(--ld-gold)]/40 bg-[var(--ld-ink)] text-[var(--ld-gold)] flex items-center justify-center font-serif-display text-xl"
        >
          {profileSrc ? (
            <img
              src={profileSrc}
              alt={vendor.company_name || ""}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <span aria-hidden>{initial}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-serif-display text-2xl text-[var(--ld-ink)] leading-tight">
              {vendor.company_name || "Unnamed vendor"}
            </h3>
            {vendor.public_liability_insurance &&
              vendor.public_liability_insurance.startsWith("Yes") && (
                <span
                  title={`Insured · ${vendor.public_liability_insurance}`}
                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-[var(--ld-gold-deep)] shrink-0"
                >
                  <ShieldCheck size={12} strokeWidth={1.6} />
                  Insured
                </span>
              )}
          </div>
        </div>
      </div>

      {/* Service tags */}
      {tags.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-1.5">
          {tags.map((t) => (
            <li
              key={t}
              className="text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 border border-[var(--ld-ink)]/30 text-[var(--ld-ink)]"
            >
              {t}
            </li>
          ))}
        </ul>
      )}

      {/* Intro */}
      {vendor.short_intro && (
        <p className="mt-5 text-[14px] leading-[1.7] text-[var(--ld-ink-muted)] line-clamp-4">
          {vendor.short_intro}
        </p>
      )}

      {/* Meta row */}
      <dl className="mt-5 grid grid-cols-1 gap-2 text-[13px] text-[var(--ld-ink-muted)]">
        {vendor.based_in && vendor.based_in.length > 0 && (
          <div className="flex items-center gap-2">
            <MapPin size={13} strokeWidth={1.5} className="text-[var(--ld-gold-deep)]" />
            <span>
              {vendor.based_in.join(", ")}
              {vendor.coverage ? ` · covers ${vendor.coverage}` : ""}
            </span>
          </div>
        )}
        {vendor.starting_price && (
          <div className="flex items-center gap-2">
            <Coins size={13} strokeWidth={1.5} className="text-[var(--ld-gold-deep)]" />
            <span>From {vendor.starting_price}</span>
          </div>
        )}
      </dl>

      {/* Contact actions */}
      <div className="mt-6 pt-5 border-t border-[var(--ld-border)] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {vendor.email && (
            <a
              href={`mailto:${vendor.email}`}
              data-testid={`vendor-email-${vendor.id}`}
              aria-label="Email"
              className="inline-flex h-9 w-9 items-center justify-center border border-[var(--ld-ink)]/40 text-[var(--ld-ink)] hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
            >
              <Mail size={14} strokeWidth={1.5} />
            </a>
          )}
          {vendor.contact_number && (
            <a
              href={`tel:${vendor.contact_number}`}
              data-testid={`vendor-phone-${vendor.id}`}
              aria-label="Call"
              className="inline-flex h-9 w-9 items-center justify-center border border-[var(--ld-ink)]/40 text-[var(--ld-ink)] hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
            >
              <Phone size={14} strokeWidth={1.5} />
            </a>
          )}
        </div>
        {vendor.email && (
          <a
            href={`mailto:${vendor.email}`}
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[var(--ld-ink)] hover:text-[var(--ld-gold-deep)] transition-colors"
          >
            Get in touch
            <ArrowUpRight size={12} strokeWidth={1.6} />
          </a>
        )}
      </div>
    </article>
  );
};

/* ---------- Page ---------- */
const VendorListing = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [serviceFilter, setServiceFilter] = useState("All");
  const [authChecked, setAuthChecked] = useState(false);
  const resultsRef = useRef(null);

  // Vendor Listing is now sign-in gated AND form-completion gated.
  // Anonymous visitors are sent to /signin; signed-in users who haven't
  // completed their form get routed to the matching form first. Pending
  // vendors (form submitted, awaiting admin approval) are kept on the
  // home page — the directory only opens to approved vendors.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { user, redirect } = await requireMember();
      if (cancelled) return;
      if (!user) {
        navigate("/signin?next=/vendor-listing", { replace: true });
        return;
      }
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }
      if (user.role === "vendor") {
        try {
          const s = await api.get("/auth/my-status");
          if (cancelled) return;
          if (s.data?.vendor_status !== "approved") {
            navigate("/", { replace: true });
            return;
          }
        } catch (e) {
          devLog("ld_listing my-status failed:", e?.message);
        }
      }
      setAuthChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/vendors");
        if (!cancelled) setVendors(res.data || []);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Couldn't load vendors.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authChecked]);

  const filtered = useMemo(() => {
    let list = vendors;
    if (serviceFilter !== "All") {
      list = list.filter((v) =>
        (v.service_types || []).some((s) => s === serviceFilter)
      );
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((v) => {
        const blob = [
          v.company_name,
          v.short_intro,
          ...(v.service_types || []),
          ...(v.based_in || []),
          v.coverage,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(q);
      });
    }
    return list;
  }, [vendors, serviceFilter, query]);

  if (!authChecked) {
    return (
      <PageShell testId="vendor-listing-page">
        <div className="px-6 md:px-12 py-24 text-center text-[var(--ld-ink-muted)] text-sm">
          Loading…
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell testId="vendor-listing-page">
      {false ? (
        <AccessGate />
      ) : (
        <>
          {/* EXPLORE VENDORS — category tiles */}
          <section
            data-testid="explore-vendors"
            className="relative px-6 md:px-12 pt-16 md:pt-20 pb-8"
          >
            <div className="mx-auto max-w-7xl">
              <h2
                data-testid="explore-vendors-heading"
                className="font-serif-display text-center text-4xl sm:text-5xl md:text-6xl text-[var(--ld-ink)] font-medium"
              >
                Explore Vendors
              </h2>
              <ul
                data-testid="explore-vendors-grid"
                className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
              >
                {EXPLORE_VENDORS.map(({ id, title, image }) => (
                  <li key={id}>
                    <Link
                      to={`/category/${id}`}
                      data-testid={`explore-tile-${id}`}
                      aria-label={`Open ${title} category`}
                      className="explore-tile group relative block w-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-[var(--ld-gold)] focus:ring-offset-4 focus:ring-offset-[var(--ld-ivory)]"
                    >
                      <img
                        src={image}
                        alt={title}
                        loading="lazy"
                        className="block w-full h-auto aspect-[4/5] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                      />
                      {/* Permanent inner vignette — adds depth & cohesion across all tiles */}
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 explore-tile-vignette"
                      />
                      {/* Hover gold wash — soft warm glow on hover */}
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 explore-tile-gold-wash"
                      />
                      {/* Hover gold ring */}
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 ring-1 ring-transparent group-hover:ring-[var(--ld-gold)]/55 transition-colors duration-500"
                      />
                      <div className="absolute top-3 right-3 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                        <span className="inline-flex h-9 w-9 items-center justify-center bg-[var(--ld-ivory)]/95 text-[var(--ld-ink)]">
                          <ArrowUpRight size={14} strokeWidth={1.6} />
                        </span>
                      </div>
                      <span className="sr-only">{title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Listing */}
          <section
            ref={resultsRef}
            className="relative px-6 md:px-12 py-16 md:py-20"
          >
            <div className="mx-auto max-w-7xl">
            <div className="text-center max-w-2xl mx-auto">
              <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
                Members Area
              </span>
              <h1
                data-testid="listing-heading"
                className="font-serif-display mt-4 text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-[var(--ld-ink)] font-medium"
              >
                Vendor Listing
              </h1>
              <p className="mt-4 text-[15px] text-[var(--ld-ink-muted)]">
                Every vendor in the directory, in one place. Filter by service
                or search to narrow down — then reach out directly.
              </p>
              <div className="mx-auto mt-6 h-px w-20 bg-[var(--ld-gold)]/60" />
            </div>

            {/* Search + filters */}
            <div className="mt-12 flex flex-col gap-4">
              <div className="relative max-w-xl mx-auto w-full">
                <Search
                  size={15}
                  strokeWidth={1.5}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ld-ink-muted)]"
                />
                <input
                  data-testid="listing-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search vendors, services, regions…"
                  className="w-full h-12 pl-11 pr-4 bg-transparent border border-[var(--ld-ink)]/30 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors duration-300"
                />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {SERVICE_FILTERS.map((s) => {
                  const active = serviceFilter === s;
                  return (
                    <button
                      key={s}
                      data-testid={`filter-${s.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                      onClick={() => setServiceFilter(s)}
                      className={
                        "px-3.5 py-1.5 text-[10px] tracking-[0.18em] uppercase border transition-colors duration-300 " +
                        (active
                          ? "bg-[var(--ld-ink)] text-[var(--ld-gold)] border-[var(--ld-ink)]"
                          : "bg-transparent text-[var(--ld-ink)] border-[var(--ld-ink)]/30 hover:border-[var(--ld-ink)]")
                      }
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Results */}
            <div className="mt-12">
              {loading && (
                <p
                  data-testid="listing-loading"
                  className="text-center text-sm text-[var(--ld-ink-muted)]"
                >
                  Loading vendors…
                </p>
              )}
              {error && (
                <p
                  data-testid="listing-error"
                  className="text-center text-sm text-[var(--ld-ink-muted)]"
                >
                  {error}
                </p>
              )}
              {!loading && !error && filtered.length === 0 && (
                <div
                  data-testid="listing-empty"
                  className="text-center max-w-md mx-auto border border-[var(--ld-border)] p-10"
                >
                  <p className="font-serif-display text-2xl text-[var(--ld-ink)]">
                    No vendors match yet.
                  </p>
                  <p className="mt-2 text-sm text-[var(--ld-ink-muted)]">
                    The directory is being curated. Check back soon — or
                    invite a favourite vendor to apply.
                  </p>
                </div>
              )}
              {!loading && !error && filtered.length > 0 && (
                <ul
                  data-testid="vendor-grid"
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                >
                  {filtered.map((v) => (
                    <li key={v.id}>
                      <VendorCard vendor={v} />
                    </li>
                  ))}
                </ul>
              )}
              {!loading && !error && (
                <p className="mt-10 text-center text-xs tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
                  {filtered.length} {filtered.length === 1 ? "vendor" : "vendors"}
                </p>
              )}
            </div>
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
};

export default VendorListing;
